// src/components/TinyEditor.js
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Grid, Box,
} from "@mui/material";

/** ===== utilidades mínimas que usamos también en MovimientosTab ===== */
const parseNum = (v) => { const n = Number(String(v ?? '').trim().replace(',', '.')); return isFinite(n) ? n : 0; };
const cmToMmStr = (cm) => `${Math.max(0, parseNum(cm) * 10).toFixed(1)}mm`;
const mmStrToCmStr = (mmStr) => (parseNum(String(mmStr).toLowerCase().replace('mm', '')) / 10).toFixed(2);

const toMmStr = (token) => {
    const t = String(token || '').trim().toLowerCase();
    if (t.endsWith('cm')) return cmToMmStr(t.slice(0, -2));
    if (t.endsWith('mm')) return `${parseNum(t.slice(0, -2)).toFixed(1)}mm`;
    return `${parseNum(t).toFixed(1)}mm`;
};

function expandPadding(pad) {
    if (!pad) return null;
    const parts = String(pad).trim().split(/\s+/).map(toMmStr);
    if (parts.length === 1) return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] };
    if (parts.length === 2) return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] };
    if (parts.length === 3) return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[1] };
    return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] };
}

function detectMarginsFromHtml(html) {
    if (!html) return null;
    const s = String(html);

    const cmt = s.match(/<!--\s*SIGIF:MARGINS\s+([^>]+)\s*-->/i);
    if (cmt) {
        const obj = {};
        cmt[1].split(';').forEach(pair => {
            const [k, v] = pair.split(':').map(x => x && x.trim());
            if (k && v) obj[k.toLowerCase()] = toMmStr(v);
        });
        if (obj.top && obj.right && obj.bottom && obj.left) return obj;
    }

    const data = s.match(/data-sigif-margins=["']([^"']+)["']/i);
    if (data) {
        const obj = {};
        data[1].split(';').forEach(pair => {
            const [k, v] = pair.split(':').map(x => x && x.trim());
            if (k && v) obj[k.toLowerCase()] = toMmStr(v);
        });
        if (obj.top && obj.right && obj.bottom && obj.left) return obj;
    }

    const stylePad = s.match(/style=["'][^"']*padding\s*:\s*([^;"']+)/i);
    if (stylePad) {
        const exp = expandPadding(stylePad[1]);
        if (exp) return exp;
    }
    return null;
}

function upsertMarginsMarker(editor, margins) {
    try {
        const doc = editor.getDoc?.();
        const body = editor.getBody?.();
        if (!doc || !body) return;

        const val = `top:${margins.top};right:${margins.right};bottom:${margins.bottom};left:${margins.left}`;
        let marker = doc.getElementById('sigif-margins');
        if (!marker) {
            marker = doc.createElement('p');
            marker.id = 'sigif-margins';
            marker.setAttribute('data-sigif-margins', val);
            marker.style.display = 'none';
            marker.style.fontSize = '0';
            marker.style.lineHeight = '0';
            body.insertBefore(marker, body.firstChild);
        } else {
            marker.setAttribute('data-sigif-margins', val);
        }
        // Sincronizamos siempre la guia visual con los margenes actuales —
        // antes habian flujos que actualizaban el marker (datos persistentes
        // del documento) sin redibujar el rectangulo de margen que ve el user.
        upsertMarginGuide(editor, margins);
    } catch { }
}

/**
 * Crea o actualiza el indicador visual de margenes dentro del body del editor.
 * Un overlay con borde dashed para marcar el area de contenido + etiquetas con
 * los valores en cm en las 4 esquinas. Mas confiable que un ::before con CSS
 * variables (el bug que reportaba el user — las lineas no coincidian con los
 * margenes configurados — era por desincronia del pseudo-elemento).
 */
function mmToCm(mmStr) {
    const n = parseFloat(String(mmStr).replace('mm', '').trim());
    return isFinite(n) ? (n / 10).toFixed(2) : '0.00';
}

function upsertMarginGuide(editor, margins) {
    try {
        const doc = editor.getDoc?.();
        const body = editor.getBody?.();
        if (!doc || !body) return;

        let guide = doc.getElementById('sigif-margin-guide');
        if (!guide) {
            guide = doc.createElement('div');
            guide.id = 'sigif-margin-guide';
            guide.setAttribute('contenteditable', 'false');
            // data-mce-bogus="all" indica a TinyMCE que este elemento NO debe
            // ser incluido en el HTML serializado (editor.getContent()), asi
            // la guia visual no termina guardada en la BD ni mandada al backend.
            guide.setAttribute('data-mce-bogus', 'all');
            // No editable, no parte del flujo del documento.
            // El body tiene position:relative (ver cssForMargins) asi que el
            // absolute se posiciona respecto del body.
            Object.assign(guide.style, {
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: '0',
                border: '1px dashed rgba(33, 116, 180, 0.65)',
                boxSizing: 'border-box',
            });

            // 4 etiquetas con los valores en cm (top, right, bottom, left)
            ['top', 'right', 'bottom', 'left'].forEach((pos) => {
                const lbl = doc.createElement('span');
                lbl.dataset.pos = pos;
                Object.assign(lbl.style, {
                    position: 'absolute',
                    background: 'rgba(33, 116, 180, 0.92)',
                    color: 'white',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '9px',
                    fontWeight: '600',
                    padding: '1px 6px',
                    borderRadius: '3px',
                    whiteSpace: 'nowrap',
                });
                if (pos === 'top')    { lbl.style.top = '-10px'; lbl.style.left = '50%'; lbl.style.transform = 'translateX(-50%)'; }
                if (pos === 'bottom') { lbl.style.bottom = '-10px'; lbl.style.left = '50%'; lbl.style.transform = 'translateX(-50%)'; }
                // Etiquetas laterales: quedan rotadas y "pegadas" al lado exterior
                // de la linea (apenas afuera del area de contenido), no sobre el
                // texto ni lejos en el margen. El -9px/+9px deja la caja rotada
                // justo del lado de afuera de la linea. (fix 2026-06-16)
                if (pos === 'left')   { lbl.style.top = '50%'; lbl.style.left = '0'; lbl.style.transform = 'translate(calc(-50% - 9px), -50%) rotate(-90deg)'; lbl.style.transformOrigin = 'center'; }
                if (pos === 'right')  { lbl.style.top = '50%'; lbl.style.right = '0'; lbl.style.transform = 'translate(calc(50% + 9px), -50%) rotate(90deg)'; lbl.style.transformOrigin = 'center'; }
                guide.appendChild(lbl);
            });
            body.appendChild(guide);
        }

        // Posicion del overlay: arriba/abajo al ras del area de contenido, pero
        // las lineas de los costados se corren hacia AFUERA (hacia el margen) para
        // que no queden sobre el texto y entorpezcan la lectura. (pedido 2026-06-16)
        const GAP_LATERAL = '8px';
        guide.style.top = margins.top;
        guide.style.bottom = margins.bottom;
        guide.style.left = `calc(${margins.left} - ${GAP_LATERAL})`;
        guide.style.right = `calc(${margins.right} - ${GAP_LATERAL})`;

        // Actualizar los textos de las etiquetas con los valores en cm.
        guide.querySelectorAll('span[data-pos]').forEach((lbl) => {
            const p = lbl.dataset.pos;
            const val = margins[p] || '0mm';
            lbl.textContent = `${mmToCm(val)} cm`;
        });
    } catch { }
}

const cssForMargins = (margins) => `
  @import url('https://fonts.googleapis.com/css2?family=Calibri&family=Cambria&family=Open+Sans&family=Roboto&family=Merriweather&display=swap');

  /* Vista A4: el body es continuo (TinyMCE no pagina nativamente) pero
     dibujamos una linea punteada fina cada 297mm como indicador de corte
     de pagina. Antes habia un GAP gris solido de 24px ahi: el texto fluia
     por encima quedando ilegible sobre el gris. Ahora la linea es de 1px
     transparente, el texto la cruza sin obstruirse y el corte sigue visible. */
  html { background: #d4d4d4; }
  body {
    position: relative;
    margin: 20px auto;
    max-width: 210mm;
    padding: ${margins.top} ${margins.right} ${margins.bottom} ${margins.left};
    background:
      repeating-linear-gradient(
        to bottom,
        transparent 0,
        transparent calc(297mm - 1px),
        rgba(0,0,0,0.22) calc(297mm - 1px),
        rgba(0,0,0,0.22) 297mm
      ),
      white;
    box-shadow:
      -10px 0 14px -4px rgba(0,0,0,0.20),
      10px 0 14px -4px rgba(0,0,0,0.20);
    font-family: 'Calibri', 'Arial', sans-serif;
    font-size: 11pt;
    line-height: 1.15;
    color: #000;
    min-height: 297mm;
  }
  /* La guia visual del area de contenido + etiquetas se crea como elemento
     real (div#sigif-margin-guide) desde upsertMarginGuide(), no como
     ::before. Asi se actualiza siempre que cambian los margenes y no depende
     de pseudo-elementos / custom properties (que tenian bugs de sincro). */
  body > *:not(#sigif-margin-guide) { position: relative; z-index: 1; }
  #sigif-margin-guide { z-index: 0; }
  p { margin: 0 0 10pt 0; line-height: 1.15; }
  h1 { font-size: 16pt; font-weight: bold; margin: 18pt 0 6pt 0; }
  h2 { font-size: 14pt; font-weight: bold; margin: 14pt 0 4pt 0; }
  h3 { font-size: 12pt; font-weight: bold; margin: 12pt 0 4pt 0; }
  table { border-collapse: collapse; width: 100%; margin: 10pt 0; }
  table, th, td { border: 1px solid #000; }
  th { background-color: #e7e7e7; font-weight: bold; padding: 5pt; }
  td { padding: 5pt; }
  ul, ol { margin-left: 36pt; margin-bottom: 10pt; }
  li { margin-bottom: 0; line-height: 1.15; }
  hr.mce-pagebreak { border: none; border-top: 1px dashed #707070; margin: 12pt 0; page-break-after: always; }
  ::selection { background-color: #0078d4; color: white; }
  a { color: #0563c1; text-decoration: underline; }

  @media print {
    body { box-shadow: none; margin: 0; padding: ${margins.top} ${margins.right} ${margins.bottom} ${margins.left}; }
    @page { size: A4; margin: 0; }
  }
`;

const ensureTinyLoaded = () =>
    new Promise((resolve, reject) => {
        if (window.tinymce && typeof window.tinymce.init === "function") return resolve();
        const candidates = ['/tinymce/tinymce.min.js', '/Ejecuciones/tinymce/tinymce.min.js'];
        (function load(i = 0) {
            if (i >= candidates.length) return reject(new Error('TinyMCE no encontrado'));
            const s = document.createElement('script');
            s.src = candidates[i];
            s.onload = () => resolve();
            s.onerror = () => { s.remove(); load(i + 1); };
            document.head.appendChild(s);
        })(0);
    });

const ensureHtml2pdf = () =>
    new Promise((resolve) => {
        if (window.html2pdf) return resolve();
        if (typeof window.process === 'undefined') window.process = { env: {} };
        const tryUrls = ['/libs/html2pdf.bundle.min.js', '/Ejecuciones/libs/html2pdf.bundle.min.js'];
        (function loadNext(i = 0) {
            if (i >= tryUrls.length) return resolve();
            const s = document.createElement('script');
            s.src = tryUrls[i];
            s.onload = () => resolve();
            s.onerror = () => loadNext(i + 1);
            document.body.appendChild(s);
        })();
    });

export default function TinyEditor({
    id = "tiny-editor",
    initialHtml = "",
    onChange = () => { },
    onReady = () => { },
    onVariablesClick = null,
}) {
    const holderRef = useRef(null);
    const marginsRef = useRef({ top: '25.4mm', right: '25.4mm', bottom: '25.4mm', left: '25.4mm' });
    const editorRef = useRef(null);
    // Tracker del último HTML que emitimos vía onChange. Sirve para no resincronizar
    // cuando el padre nos devuelve por props lo mismo que acabamos de emitir — eso
    // disparaba setContent() y movía el cursor al inicio mientras el usuario tipeaba.
    const lastEmittedRef = useRef("");

    // Callback del botón "Variables" del toolbar. Se mantiene fresco vía ref para
    // que el botón (registrado una sola vez en setup) siempre llame a la versión
    // actual. Si el contenedor no pasa el prop, el botón no se registra.
    const onVariablesClickRef = useRef(onVariablesClick);
    useEffect(() => { onVariablesClickRef.current = onVariablesClick; }, [onVariablesClick]);

    // Dialog de márgenes personalizados — usamos MUI en vez del windowManager de
    // TinyMCE porque sus inputs tenían un bug que no permitía editar los valores.
    const [marginsDialog, setMarginsDialog] = useState({ open: false, top: '2.54', right: '2.54', bottom: '2.54', left: '2.54' });

    // Helper: aplica margenes al body + sincroniza la guia visual.
    // La guia es un <div> real con borde dashed y etiquetas con valores en cm,
    // no un pseudo-elemento — antes el ::before podia desincronizarse y mostrar
    // las lineas fuera de los margenes reales.
    const setBodyMargins = (body, mm) => {
        if (!body) return;
        body.style.padding = `${mm.top} ${mm.right} ${mm.bottom} ${mm.left}`;
    };

    const aplicarMargenes = useCallback((mm) => {
        marginsRef.current = mm;
        const ed = editorRef.current;
        if (!ed) return;
        try {
            setBodyMargins(ed.getBody(), mm);
            upsertMarginsMarker(ed, mm);
            upsertMarginGuide(ed, mm);
        } catch { }
    }, []);

    useEffect(() => {
        let mounted = true;

        async function init() {
            // 1) limpiar instancias previas (forzamos)
            if (window.tinymce?.remove) { try { window.tinymce.remove(); } catch { } }
            if (window.tinymce?.get?.(id)) { try { window.tinymce.remove(`#${id}`); } catch { } }

            // 2) cargar Tiny
            await ensureTinyLoaded();
            if (!mounted) return;

            // 2.1) template interno del textarea si no existe
            if (!document.getElementById(id)) {
                const ta = document.createElement("textarea");
                ta.id = id;
                ta.defaultValue = initialHtml || "";
                holderRef.current.appendChild(ta);
            }

            // 3) márgenes desde contenido (si hay)
            const detected = detectMarginsFromHtml(initialHtml);
            if (detected) marginsRef.current = detected;

            // 4) init
            const editors = await window.tinymce.init({
                selector: `#${id}`,
                promotion: false,
                language: 'es_MX',
                language_url: '/tinymce/langs/es_MX.js',
                cache_suffix: '?v=2.0.0',
                base_url: '/tinymce',
                suffix: '.min',

                content_style: cssForMargins(marginsRef.current),

                formats: {
                    // párrafos
                    sangriaPrimera: { block: 'p', styles: { textIndent: '12.7mm' } },
                    espacioDespues6: { block: 'p', styles: { marginBottom: '6pt' } },
                    espacioDespues12: { block: 'p', styles: { marginBottom: '12pt' } },
                    lineheight1: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', styles: { lineHeight: '1.0' } },
                    lineheight15: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', styles: { lineHeight: '1.5' } },
                    lineheight2: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li', styles: { lineHeight: '2.0' } },
                    // tipografías
                    font_times: { inline: 'span', styles: { fontFamily: 'Times New Roman,Times,serif' } },
                    font_arial: { inline: 'span', styles: { fontFamily: 'Arial,Helvetica,sans-serif' } },
                    font_calibri: { inline: 'span', styles: { fontFamily: 'Calibri,Arial,Helvetica,sans-serif' } },
                    font_cambria: { inline: 'span', styles: { fontFamily: 'Cambria,Times New Roman,serif' } },
                    font_courier: { inline: 'span', styles: { fontFamily: 'Courier New,Courier,monospace' } },
                    font_georgia: { inline: 'span', styles: { fontFamily: 'Georgia,serif' } },
                    font_verdana: { inline: 'span', styles: { fontFamily: 'Verdana,Geneva,sans-serif' } },
                    font_tahoma: { inline: 'span', styles: { fontFamily: 'Tahoma,Geneva,sans-serif' } },
                },

                plugins: [
                    'advlist autolink lists link anchor charmap',
                    'searchreplace visualblocks visualchars wordcount',
                    'image media table pagebreak nonbreaking',
                    'insertdatetime directionality fullscreen preview',
                    'code quickbars autoresize help emoticons'
                ].join(' '),

                menubar: 'file edit view insert format tools table',
                menu: {
                    file: { title: 'Archivo', items: 'newdocument | templates | preview print exportpdf | wordcount' },
                    edit: { title: 'Editar', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
                    view: { title: 'Ver', items: 'code | visualaid visualchars visualblocks | preview fullscreen' },
                    insert: { title: 'Insertar', items: 'image link media pagebreak | charmap emoticons hr | insertdatetime' },
                    format: { title: 'Formato', items: 'bold italic underline strikethrough superscript subscript | blocks fontfamily fontsize align lineheight | forecolor backcolor | removeformat' },
                    tools: { title: 'Herramientas', items: 'wordcount code' },
                    table: { title: 'Tabla', items: 'inserttable | cell row column | tableprops deletetable' }
                },

                toolbar_mode: 'sliding',
                toolbar_sticky: true,
                toolbar_sticky_offset: 0,
                toolbar: [
                    'newdocument undo redo | cut copy paste | searchreplace | templates variables | preview print exportpdf',
                    'fontselect fontsizeinput fontsizeselect | bold italic underline strikethrough | fullscreen | forecolor backcolor | removeformat',
                    'blocks | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | lineheight',
                    'table image link | charmap emoticons | pagebreak | insertdatetime | estilosdoc tipografia | code help'
                ].join(' | '),

                block_formats: 'Normal=p; Título 1=h1; Título 2=h2; Título 3=h3; Cita=blockquote',
                font_family_formats: [
                    'Calibri=Calibri,Arial,sans-serif',
                    'Times New Roman=Times New Roman,Times,serif',
                    'Arial=Arial,Helvetica,sans-serif',
                    'Cambria=Cambria,Times New Roman,serif',
                    'Verdana=Verdana,Geneva,sans-serif',
                    'Tahoma=Tahoma,Geneva,sans-serif',
                    'Courier New=Courier New,Courier,monospace',
                    'Georgia=Georgia,serif'
                ].join(';'),
                fontsize_formats: '8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt 26pt 28pt 36pt 48pt 72pt',
                lineheight_formats: '1.0 1.15 1.5 2.0 2.5 3.0',

                paste_as_text: false,
                paste_data_images: true,

                automatic_uploads: false,
                file_picker_types: 'image',
                image_advtab: true,
                image_caption: true,
                file_picker_callback: (cb, value, meta) => {
                    if (meta.filetype !== 'image') return;
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = function () {
                        const file = this.files[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = () => cb(reader.result, { alt: file.name });
                        reader.readAsDataURL(file);
                    };
                    input.click();
                },

                table_default_attributes: { border: '1' },
                table_default_styles: { 'border-collapse': 'collapse', 'width': '100%' },
                table_grid: true,
                table_resize_bars: true,
                table_style_by_css: true,

                autoresize_bottom_margin: 50,
                min_height: 400,
                max_height: 800,

                branding: false,
                elementpath: true,
                resize: false,
                statusbar: true,

                init_instance_callback: (editor) => {

                    // set contenido inicial
                    if (initialHtml) editor.setContent(initialHtml);
                    // Inicializa el tracker con la versión normalizada por TinyMCE — sin esto
                    // el primer sync por props detecta diferencia y dispara setContent.
                    try { lastEmittedRef.current = editor.getContent(); } catch { }

                    // márgenes desde contenido (si había)
                    const m = detectMarginsFromHtml(initialHtml) || marginsRef.current;
                    marginsRef.current = m;
                    try {
                        setBodyMargins(editor.getBody(), m);
                        upsertMarginsMarker(editor, m);
                    } catch { }

                    // callbacks externos
                    onReady({
                        getContent: () => editor.getContent(),
                        setContent: (html) => {
                            editor.setContent(html || '');
                            const detected = detectMarginsFromHtml(html || '');
                            if (detected) {
                                marginsRef.current = detected;
                                setBodyMargins(editor.getBody(), detected);
                                upsertMarginsMarker(editor, detected);
                            }
                        },
                        insert: (htmlOrText) => editor.insertContent(htmlOrText || ''),
                        print: () => {
                            const html = editor.getContent({ format: 'html' });
                            const style = cssForMargins(marginsRef.current);
                            const iframe = document.createElement('iframe');
                            iframe.style.position = 'fixed';
                            iframe.style.right = '0';
                            iframe.style.bottom = '0';
                            iframe.style.width = '0';
                            iframe.style.height = '0';
                            iframe.style.border = '0';
                            iframe.style.visibility = 'hidden';
                            document.body.appendChild(iframe);

                            const doc = iframe.contentWindow.document;
                            doc.open();
                            doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>${style}</style></head><body>${html}</body></html>`);
                            doc.close();
                            setTimeout(() => {
                                iframe.contentWindow.focus();
                                iframe.contentWindow.print();
                                setTimeout(() => document.body.removeChild(iframe), 200);
                            }, 50);
                        },
                        exportPdf: async () => {
                            await ensureHtml2pdf();
                            if (!window.html2pdf) return;
                            const html = editor.getContent({ format: 'html' });
                            const wrapper = `<html><head><meta charset="utf-8"><style>${cssForMargins(marginsRef.current)}</style></head><body>${html}</body></html>`;
                            const opt = {
                                margin: 0,
                                filename: 'escrito.pdf',
                                image: { type: 'jpeg', quality: 0.98 },
                                html2canvas: { scale: 2, useCORS: true },
                                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                                pagebreak: { mode: ['css', 'legacy'] },
                            };
                            window.html2pdf().from(wrapper).set(opt).save();
                        }
                    });

                    // change → onChange
                    let t;
                    editor.on('input change undo redo', () => {
                        clearTimeout(t);
                        t = setTimeout(() => {
                            const content = editor.getContent();
                            lastEmittedRef.current = content;
                            onChange(content);
                        }, 250);
                    });
                },

                setup: (editor) => {
                    // botones/menús custom iguales a MovimientosTab
                    const allSidesCm = (cm) => ({
                        top: cmToMmStr(cm),
                        right: cmToMmStr(cm),
                        bottom: cmToMmStr(cm),
                        left: cmToMmStr(cm)
                    });

                    // Fix para dropdowns en fullscreen
                    editor.on('FullscreenStateChanged', (e) => {
                        if (e.state) {
                            // Entró en fullscreen - aumentar z-index de los dropdowns
                            const style = document.createElement('style');
                            style.id = 'tinymce-fullscreen-fix';
                            style.innerHTML = `
                .tox-tinymce-aux {
                    z-index: 1300 !important;
                }
                .tox-dialog-wrap {
                    z-index: 1301 !important;
                }
                .tox.tox-tinymce.tox-fullscreen {
                    z-index: 1299 !important;
                }
            `;
                            document.head.appendChild(style);
                        } else {
                            // Salió de fullscreen - remover el fix
                            const style = document.getElementById('tinymce-fullscreen-fix');
                            if (style) style.remove();
                        }
                    });

                    editor.ui.registry.addMenuButton('estilosdoc', {
                        text: 'Estilos',
                        tooltip: 'Estilos del documento',
                        fetch: (cb) => cb([
                            {
                                type: 'menuitem', text: 'Márgenes normales (2.54 cm)', onAction: () => {
                                    marginsRef.current = allSidesCm(2.54);
                                    setBodyMargins(editor.getBody(), marginsRef.current);
                                    upsertMarginsMarker(editor, marginsRef.current);
                                }
                            },
                            {
                                type: 'menuitem', text: 'Márgenes moderados (1.90 cm)', onAction: () => {
                                    marginsRef.current = allSidesCm(1.90);
                                    setBodyMargins(editor.getBody(), marginsRef.current);
                                    upsertMarginsMarker(editor, marginsRef.current);
                                }
                            },
                            {
                                type: 'menuitem', text: 'Márgenes estrechos (1.27 cm)', onAction: () => {
                                    marginsRef.current = allSidesCm(1.27);
                                    setBodyMargins(editor.getBody(), marginsRef.current);
                                    upsertMarginsMarker(editor, marginsRef.current);
                                }
                            },
                            { type: 'separator' },
                            {
                                type: 'menuitem', text: 'Márgenes personalizados…', onAction: () => {
                                    // Abrimos el Dialog MUI (los inputs del windowManager de
                                    // TinyMCE no aceptaban edición — bug conocido).
                                    const cur = marginsRef.current;
                                    setMarginsDialog({
                                        open: true,
                                        top: mmStrToCmStr(cur.top),
                                        right: mmStrToCmStr(cur.right),
                                        bottom: mmStrToCmStr(cur.bottom),
                                        left: mmStrToCmStr(cur.left),
                                    });
                                }
                            },
                            { type: 'separator' },
                            {
                                type: 'menuitem', text: '🗜️ Compactar para 1 página', onAction: () => {
                                    // Aplica de un click: márgenes 15mm + line-height 1.0 +
                                    // margin-bottom 4pt en cada párrafo. Pensado para que un
                                    // escrito que se pasa por poco entre en una sola hoja A4.
                                    // Es reversible con Ctrl+Z.
                                    marginsRef.current = { top: '15mm', right: '15mm', bottom: '15mm', left: '15mm' };
                                    const b = editor.getBody();
                                    if (b) {
                                        setBodyMargins(b, marginsRef.current);
                                        const elems = b.querySelectorAll('p, li, td, th, div, h1, h2, h3, h4, h5, h6');
                                        elems.forEach(e => {
                                            e.style.lineHeight = '1.0';
                                            e.style.marginBottom = '4pt';
                                        });
                                    }
                                    upsertMarginsMarker(editor, marginsRef.current);
                                    editor.undoManager.add();
                                }
                            },
                            { type: 'separator' },
                            { type: 'menuitem', text: 'Sangría primera línea', onAction: () => editor.execCommand('mceToggleFormat', false, 'sangriaPrimera') },
                            { type: 'menuitem', text: 'Espacio después (6 pt)', onAction: () => editor.execCommand('mceToggleFormat', false, 'espacioDespues6') },
                            { type: 'menuitem', text: 'Espacio después (12 pt)', onAction: () => editor.execCommand('mceToggleFormat', false, 'espacioDespues12') },
                        ])
                    });

                    editor.ui.registry.addMenuButton('tipografia', {
                        text: 'Tipografía',
                        tooltip: 'Cambiar tipo de letra',
                        fetch: (cb) => cb([
                            { type: 'menuitem', text: 'Times New Roman', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_times'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Arial', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_arial'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Calibri', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_calibri'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Cambria', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_cambria'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Courier New', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_courier'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Georgia', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_georgia'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Verdana', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_verdana'); editor.selection.moveToBookmark(bm); } },
                            { type: 'menuitem', text: 'Tahoma', onAction: () => { const bm = editor.selection.getBookmark(); editor.execCommand('mceToggleFormat', false, 'font_tahoma'); editor.selection.moveToBookmark(bm); } },
                        ])
                    });

                    editor.ui.registry.addMenuButton('templates', {
                        text: 'Plantillas',
                        tooltip: 'Plantillas de documento',
                        fetch: (cb) => cb([
                            {
                                type: 'menuitem', text: '📄 Carta Formal', onAction: () => {
                                    editor.setContent(`
                  <p style="text-align: right;">[Ciudad], [Fecha]</p>
                  <p>&nbsp;</p>
                  <p><strong>Señor/a</strong><br/>[Nombre del destinatario]<br/>[Cargo]<br/>[Empresa]<br/><strong>Presente</strong></p>
                  <p>&nbsp;</p>
                  <p>De mi consideración:</p>
                  <p>[Cuerpo de la carta]</p>
                  <p>&nbsp;</p>
                  <p>Sin otro particular, saludo a Ud. atentamente.</p>
                  <p>&nbsp;</p><p>&nbsp;</p>
                  <p style="text-align: center;">_____________________<br/>[Nombre]<br/>[DNI]</p>
                `);
                                    editor.undoManager.add();
                                }
                            },
                            {
                                type: 'menuitem', text: '📋 Escrito Judicial', onAction: () => {
                                    editor.setContent(`
                  <p><strong>SEÑOR JUEZ:</strong></p>
                  <p>&nbsp;</p>
                  <p style="text-indent: 40px;">[Texto del escrito]</p>
                  <p>&nbsp;</p><p style="text-align: center;">SERÁ JUSTICIA</p>
                `);
                                    editor.undoManager.add();
                                }
                            },
                        ])
                    });

                    // Imprimir
                    editor.ui.registry.addButton('print', {
                        icon: 'print',
                        tooltip: 'Imprimir (Ctrl+P)',
                        onAction: () => {
                            const html = editor.getContent({ format: 'html' });
                            const style = cssForMargins(marginsRef.current);
                            const iframe = document.createElement('iframe');
                            iframe.style.position = 'fixed';
                            iframe.style.right = '0';
                            iframe.style.bottom = '0';
                            iframe.style.width = '0';
                            iframe.style.height = '0';
                            iframe.style.border = '0';
                            iframe.style.visibility = 'hidden';
                            document.body.appendChild(iframe);
                            const doc = iframe.contentWindow.document;
                            doc.open();
                            doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>${style}</style></head><body>${html}</body></html>`);
                            doc.close();
                            setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 200); }, 50);
                        }
                    });

                    // PDF
                    editor.ui.registry.addButton('exportpdf', {
                        icon: 'save',
                        tooltip: 'Exportar como PDF',
                        onAction: async () => {
                            await ensureHtml2pdf();
                            if (!window.html2pdf) return;
                            const html = editor.getContent({ format: 'html' });
                            const wrapper = `<html><head><meta charset="utf-8"><style>${cssForMargins(marginsRef.current)}</style></head><body>${html}</body></html>`;
                            const opt = { margin: 0, filename: 'escrito.pdf', image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }, pagebreak: { mode: ['css', 'legacy'] } };
                            window.html2pdf().from(wrapper).set(opt).save();
                        }
                    });

                    // Botón "Variables" — abre el panel lateral de variables del
                    // documento. Solo se registra si el contenedor pasó el callback
                    // (Documentos sí lo usa; MovimientosTab no → el botón no aparece).
                    // Funciona también en pantalla completa: al estar en el toolbar
                    // del editor, el botón sigue visible dentro del fullscreen.
                    if (onVariablesClick) {
                        editor.ui.registry.addButton('variables', {
                            text: 'Variables',
                            icon: 'template',
                            tooltip: 'Insertar variables del documento',
                            onAction: () => { onVariablesClickRef.current?.(); }
                        });
                    }
                },

                valid_elements: '*[*]',
                extended_valid_elements: '*[*]',
                quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote',
                quickbars_insert_toolbar: 'quickimage quicktable',
                powerpaste_word_import: 'merge',
                help_tabs: ['shortcuts', 'keyboardnav'],
                license_key: 'gpl',
            });

            if (mounted && editors?.length) {
                editorRef.current = editors[0];
            }
        }

        init();

        return () => {
            mounted = false;
            if (window.tinymce?.get?.(id)) {
                try { window.tinymce.remove(`#${id}`); } catch { }
            } else if (window.tinymce?.remove) {
                try { window.tinymce.remove(); } catch { }
            }
            editorRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Sync externo → editor.
    //
    // El padre dispara onChange → guarda el HTML en su state → ese mismo HTML
    // vuelve por props como `initialHtml`. TinyMCE normaliza el HTML (atributos,
    // comillas, espacios) en getContent(), así que `cur !== initialHtml` casi
    // siempre da true aunque el contenido sea equivalente. Antes eso disparaba
    // un setContent() que reseteaba el cursor al inicio mientras tipeabas — y
    // robaba el foco si había un diálogo abierto (Márgenes personalizados).
    //
    // Solución: ignorar el sync cuando lo que llega por props es eco de lo que
    // acabamos de emitir, o mientras el editor tiene foco activo.
    useEffect(() => {
        const ed = editorRef.current;
        if (!ed) return;
        const incoming = String(initialHtml || "");
        // Eco de nuestro último onChange — el padre nos devuelve lo nuestro.
        if (incoming === lastEmittedRef.current) return;
        // El usuario está editando — no interrumpir.
        try { if (ed.hasFocus && ed.hasFocus()) return; } catch { }

        const cur = ed.getContent();
        if (String(cur || "") !== incoming) {
            ed.setContent(initialHtml || "");
            lastEmittedRef.current = ed.getContent();
            const detected = detectMarginsFromHtml(initialHtml || "");
            if (detected) {
                marginsRef.current = detected;
                try {
                    setBodyMargins(ed.getBody(), detected);
                    upsertMarginsMarker(ed, detected);
                } catch { }
            }
        }
    }, [initialHtml]);

    const handleAplicarMargenes = () => {
        const mm = {
            top: cmToMmStr(marginsDialog.top),
            right: cmToMmStr(marginsDialog.right),
            bottom: cmToMmStr(marginsDialog.bottom),
            left: cmToMmStr(marginsDialog.left),
        };
        aplicarMargenes(mm);
        setMarginsDialog((d) => ({ ...d, open: false }));
    };

    return (
        <>
            <div ref={holderRef} />
            <Dialog
                open={marginsDialog.open}
                onClose={() => setMarginsDialog((d) => ({ ...d, open: false }))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '4px' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1769aa' }}>
                    Márgenes personalizados (cm)
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ pt: 1 }}>
                        <Grid container spacing={2}>
                            <Grid size={6}>
                                <TextField
                                    fullWidth size="small" label="Superior"
                                    value={marginsDialog.top}
                                    onChange={(e) => setMarginsDialog((d) => ({ ...d, top: e.target.value }))}
                                    autoFocus
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    fullWidth size="small" label="Inferior"
                                    value={marginsDialog.bottom}
                                    onChange={(e) => setMarginsDialog((d) => ({ ...d, bottom: e.target.value }))}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    fullWidth size="small" label="Izquierdo"
                                    value={marginsDialog.left}
                                    onChange={(e) => setMarginsDialog((d) => ({ ...d, left: e.target.value }))}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    fullWidth size="small" label="Derecho"
                                    value={marginsDialog.right}
                                    onChange={(e) => setMarginsDialog((d) => ({ ...d, right: e.target.value }))}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMarginsDialog((d) => ({ ...d, open: false }))}>
                        Cancelar
                    </Button>
                    <Button onClick={handleAplicarMargenes} variant="contained" sx={{ bgcolor: '#1769aa' }}>
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
